/**
 * SkillForm Component
 * Form for creating and editing skills
 * Matches admin interface fields: skill_id, name, description, command, display_order, enabled
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { Skill, CreateSkillRequest, UpdateSkillRequest } from '@/types/skills';

interface SkillFormProps {
  skill?: Skill | null;
  onSubmit: (data: CreateSkillRequest | UpdateSkillRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

interface FormData {
  skill_id: string;
  name: string;
  description: string;
  command: string;
  room_mode: boolean;
  en_name: string;
  en_description: string;
  en_command: string;
  display_order: number;
  enabled: boolean;
}

export const SkillForm: React.FC<SkillFormProps> = ({
  skill,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { t } = useI18n();
  const isEditing = !!skill;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      skill_id: skill?.skill_id || '',
      name: skill?.name || '',
      description: skill?.description || '',
      command: skill?.command || '',
      room_mode: skill?.config?.room_mode === true,
      en_name: skill?.translations?.en?.name || '',
      en_description: skill?.translations?.en?.description || '',
      en_command: skill?.translations?.en?.command || '',
      display_order: skill?.display_order ?? 0,
      enabled: skill?.enabled ?? true,
    },
  });

  useEffect(() => {
    reset({
      skill_id: skill?.skill_id || '',
      name: skill?.name || '',
      description: skill?.description || '',
      command: skill?.command || '',
      room_mode: skill?.config?.room_mode === true,
      en_name: skill?.translations?.en?.name || '',
      en_description: skill?.translations?.en?.description || '',
      en_command: skill?.translations?.en?.command || '',
      display_order: skill?.display_order ?? 0,
      enabled: skill?.enabled ?? true,
    });
  }, [skill, reset]);

  const handleFormSubmit = (data: FormData) => {
    const { en_name, en_description, en_command, room_mode, ...rest } = data;
    // Frontend requires EN name + command; backend keeps translations optional
    // so existing skills with empty EN stay valid. Merge into existing
    // translations (backend update replaces the column wholesale) to avoid
    // wiping any non-EN languages.
    const translations = {
      ...(skill?.translations || {}),
      en: {
        name: en_name.trim(),
        description: en_description.trim(),
        command: en_command.trim(),
      },
    };
    // For update, don't include skill_id since it can't be changed
    if (isEditing) {
      const { skill_id: _, ...updateData } = rest;
      onSubmit({
        ...updateData,
        translations,
        ...(skill.config?.artifact_type === 'tarot_reading'
          ? { config: { ...skill.config, room_mode } }
          : {}),
      });
    } else {
      onSubmit({ ...rest, translations });
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Skill ID - required for create, readonly for edit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('agentConfig.skills.skillId')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder={t('agentConfig.skills.skillIdPlaceholder')}
          disabled={isEditing}
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${isEditing ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}
            ${
              errors.skill_id
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('skill_id', {
            required: !isEditing ? t('agentConfig.skills.skillIdRequired') : false,
            pattern: {
              value: /^[a-zA-Z0-9_-]+$/,
              message: t('agentConfig.skills.skillIdPattern'),
            },
          })}
        />
        <p className="mt-1 text-xs text-gray-500">
          {isEditing ? t('agentConfig.skills.skillIdReadonlyHint') : t('agentConfig.skills.skillIdHint')}
        </p>
        {errors.skill_id && (
          <p className="mt-1 text-sm text-red-600">{errors.skill_id.message}</p>
        )}
      </div>

      <Input
        label={t('agentConfig.skills.name')}
        placeholder={t('agentConfig.skills.namePlaceholder')}
        error={errors.name?.message}
        required
        {...register('name', {
          required: t('agentConfig.skills.nameRequired'),
          minLength: {
            value: 1,
            message: t('agentConfig.skills.nameRequired'),
          },
        })}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('agentConfig.skills.description')}
        </label>
        <textarea
          placeholder={t('agentConfig.skills.descriptionPlaceholder')}
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors resize-none h-20
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              errors.description
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('description')}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('agentConfig.skills.command')} <span className="text-red-500">*</span>
        </label>
        <textarea
          placeholder={t('agentConfig.skills.commandPlaceholder')}
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors resize-none h-20
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              errors.command
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('command', {
            required: t('agentConfig.skills.commandRequired'),
          })}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('agentConfig.skills.commandHint')}
        </p>
        {errors.command && (
          <p className="mt-1 text-sm text-red-600">{errors.command.message}</p>
        )}
      </div>

      {/* English translation — required on the frontend only */}
      <div className="pt-2 border-t border-gray-200">
        <p className="text-sm font-semibold text-gray-700">
          {t('agentConfig.skills.enSectionTitle')}
        </p>
        <p className="mt-1 mb-3 text-xs text-gray-500">
          {t('agentConfig.skills.enSectionHint')}
        </p>

        <Input
          label={t('agentConfig.skills.enName')}
          placeholder={t('agentConfig.skills.enNamePlaceholder')}
          error={errors.en_name?.message}
          required
          {...register('en_name', {
            required: t('agentConfig.skills.enNameRequired'),
            validate: (v) =>
              v.trim().length > 0 || t('agentConfig.skills.enNameRequired'),
          })}
        />

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('agentConfig.skills.enDescription')}
          </label>
          <textarea
            placeholder={t('agentConfig.skills.enDescriptionPlaceholder')}
            className={`
              w-full px-3 py-2 border rounded-lg text-sm transition-colors resize-none h-20
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${
                errors.en_description
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }
            `}
            {...register('en_description')}
          />
          {errors.en_description && (
            <p className="mt-1 text-sm text-red-600">
              {errors.en_description.message}
            </p>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('agentConfig.skills.enCommand')} <span className="text-red-500">*</span>
          </label>
          <textarea
            placeholder={t('agentConfig.skills.enCommandPlaceholder')}
            className={`
              w-full px-3 py-2 border rounded-lg text-sm transition-colors resize-none h-20
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${
                errors.en_command
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }
            `}
            {...register('en_command', {
              required: t('agentConfig.skills.enCommandRequired'),
              validate: (v) =>
                v.trim().length > 0 || t('agentConfig.skills.enCommandRequired'),
            })}
          />
          {errors.en_command && (
            <p className="mt-1 text-sm text-red-600">
              {errors.en_command.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('agentConfig.skills.displayOrder')}
        </label>
        <input
          type="number"
          min="0"
          placeholder="0"
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              errors.display_order
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('display_order', {
            valueAsNumber: true,
            min: { value: 0, message: t('agentConfig.skills.displayOrderMin') },
          })}
        />
        <p className="mt-1 text-xs text-gray-500">{t('agentConfig.skills.displayOrderHint')}</p>
        {errors.display_order && (
          <p className="mt-1 text-sm text-red-600">{errors.display_order.message}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="enabled"
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          {...register('enabled')}
        />
        <label htmlFor="enabled" className="text-sm text-gray-700">
          {t('agentConfig.skills.enableSkill')}
        </label>
      </div>

      {isEditing && skill.config?.artifact_type === 'tarot_reading' && (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="room_mode"
            className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            {...register('room_mode')}
          />
          <div>
            <label htmlFor="room_mode" className="text-sm text-gray-700">
              {t('agentConfig.skills.openTarotRoomDirectly')}
            </label>
            <p className="text-xs text-gray-500">
              {t('agentConfig.skills.openTarotRoomDirectlyHint')}
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" type="button" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" loading={isSubmitting}>
          {skill ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  );
};
